import { memo, useCallback, useRef, useState } from 'react'
import { Avatar, Box, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Security } from '../../proto/generated/security'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import { getSecurityKey } from '../../util/security'
import { makeStyles } from 'tss-react/mui'
import { generatePath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import ViewMenuGridIcon from '../../assets/icons/ViewMenuGridIcon'
import { NonInteractiveTooltip } from '../custom'
import { useDispatch, useSelector } from 'react-redux'
import { setSecuritySearchOpen } from '../../reducer/home.reducer'
import { selectFinancialPanelOpen } from '../../selectors/home'
import useSelectView from '../../hooks/useSelectView'
import useSecurityViewSelect from '../../hooks/useSecurityViewSelect'
import { SecurityViewMenu } from '../securitySearch/SecurityViewMenu'

const useStyles = makeStyles()((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        padding: '8px 16px',
        boxSizing: 'border-box',
    },
    logoBox: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
    },
    titleStack: {
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
    },
    symbolLink: {
        flexShrink: 0,
    },
    companyName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    viewMenuAction: {
        flexShrink: 0,
        marginLeft: theme.spacing(0.5),
    },
    avatar: {
        width: 24,
        height: 24,
        fontSize: 18,
        color: 'white'
    },
    link: {
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            color: theme.palette.info.dark
        }
    },
    openIcon: {
        fontSize: 14,
        marginLeft: theme.spacing(1),
        color: theme.palette.success.dark,
        '&:hover': {
            color: 'inherit'
        }
    },
    secondaryTextColor: {
        color: theme.palette.text.secondary
    },
    actionButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover
        }
    }
}))

const SecurityOverviewHeader = ({ currentVisibleOverviewSecurity }: { currentVisibleOverviewSecurity: Security }) => {
    const { classes } = useStyles()
    const theme = useTheme()
    const dispatch = useDispatch()
    const view = useSelectView()
    const isFinancialDataOpen = useSelector(selectFinancialPanelOpen)
    const viewMenuButtonRef = useRef<HTMLButtonElement>(null)
    const [isViewMenuOpen, setIsViewMenuOpen] = useState(false)

    const handleViewMenuClose = useCallback(() => {
        setIsViewMenuOpen(false)
    }, [])

    const onViewSelect = useSecurityViewSelect({
        security: currentVisibleOverviewSecurity,
        onComplete: handleViewMenuClose,
    })

    const openSecuritySearch = useCallback(() => {
        dispatch(setSecuritySearchOpen({ open: true, key: undefined, isExpression: false }))
    }, [dispatch])

    const handleOverviewSymbolClick = useCallback(() => {
        if (isFinancialDataOpen) {
            openSecuritySearch()
            return
        }
        handleOverviewSecurityClick(currentVisibleOverviewSecurity)
    }, [currentVisibleOverviewSecurity, isFinancialDataOpen, openSecuritySearch])

    const handleCompanyNameClick = useCallback(() => {
        if (isFinancialDataOpen) {
            openSecuritySearch()
        }
    }, [isFinancialDataOpen, openSecuritySearch])

    const handleViewMenuToggle = useCallback(() => {
        setIsViewMenuOpen((open) => !open)
    }, [])

    const showViewMenuAction = currentVisibleOverviewSecurity.instrument !== 'IDX'

    return currentVisibleOverviewSecurity ? (
        <Box className={classes.container}>
            <Box className={classes.logoBox}>
                {currentVisibleOverviewSecurity?.logoUrl ? (
                    <Avatar alt="Icon" src={`${LOGOS_BASE_URL}${currentVisibleOverviewSecurity.logoUrl}.svg`} className={classes.avatar} />
                ) : (
                    <Avatar
                        alt="Icon"
                        className={classes.avatar}
                        style={{
                            backgroundColor: getColorForSymbol(currentVisibleOverviewSecurity.symbol || currentVisibleOverviewSecurity.code, theme.palette.background.default)
                        }}
                    >
                        {(currentVisibleOverviewSecurity.symbol || currentVisibleOverviewSecurity.code)
                            .split(' ')
                            .map((word) => word[0]?.toUpperCase())
                            .slice(0, 2)
                            .join('')}
                    </Avatar>
                )}
                <Stack direction="row" gap={1} pl={1} alignItems="baseline" className={classes.titleStack}>
                    <Link
                        data-event={isFinancialDataOpen ? 'Symbol Search Clicked' : 'Symbol Chart Clicked'}
                        data-event-symbol-name={currentVisibleOverviewSecurity.symbol}
                        data-event-source={'overview'}
                        className={`${classes.link} ${classes.symbolLink}`}
                        onClick={handleOverviewSymbolClick}
                    >
                        <Typography variant="body2" component="span" noWrap>
                            {currentVisibleOverviewSecurity.symbol}
                        </Typography>
                        {!isFinancialDataOpen && <OpenInNewIcon className={classes.openIcon} />}
                    </Link>
                    <Typography className={classes.secondaryTextColor} sx={{ flexShrink: 0 }}>
                        {' \u2022 '}
                    </Typography>
                    <Typography
                        variant="body2"
                        className={`${classes.secondaryTextColor} ${classes.companyName}`}
                        onClick={handleCompanyNameClick}
                        sx={isFinancialDataOpen ? { cursor: 'pointer', '&:hover': { color: 'text.primary' } } : undefined}
                    >
                        {currentVisibleOverviewSecurity?.displayName} {currentVisibleOverviewSecurity?.instrument === 'IDX' && ' Index'}
                    </Typography>
                </Stack>
            </Box>
            {showViewMenuAction && view && (
                <Box className={classes.viewMenuAction}>
                    <NonInteractiveTooltip placement="top" arrow title="Switch view">
                        <IconButton
                            ref={viewMenuButtonRef}
                            className={classes.actionButton}
                            onClick={handleViewMenuToggle}
                            aria-label="Switch view"
                            aria-haspopup="menu"
                            aria-expanded={isViewMenuOpen}
                            sx={{ width: 32, height: 32, p: 0 }}
                        >
                            <ViewMenuGridIcon sx={{ fontSize: '28px' }} />
                        </IconButton>
                    </NonInteractiveTooltip>
                    <SecurityViewMenu
                        open={isViewMenuOpen}
                        onClose={handleViewMenuClose}
                        anchorReference="anchorEl"
                        anchorEl={viewMenuButtonRef.current}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        clickAwayIgnoreRefs={[viewMenuButtonRef]}
                        dataEventSource="overview"
                        view={view}
                        onViewSelect={onViewSelect}
                        security={currentVisibleOverviewSecurity}
                    />
                </Box>
            )}
        </Box>
    ) : null
}

export default memo(SecurityOverviewHeader)

export const handleOverviewSecurityClick = (security: Security | string | undefined) => {
    if (!security) {
        return null
    }
    const origin = window.location.origin
    return window.open(origin + `${generatePath(ROUTES.symbol, { symbol: typeof security === 'string' ? security : getSecurityKey(security.exchange, security.code) })}`, '_blank')
}
